import React, { createContext, useContext, useEffect, useState, useRef, useCallback } from "react";
import axios from "axios";
import { useSocket } from "@/lib/useSocket";

const AppContext = createContext(null);
export const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
axios.defaults.withCredentials = true;

const getInitialTheme = () => {
  const stored = localStorage.getItem("sb_theme");
  if (stored === "light" || stored === "dark") return stored;
  return window.matchMedia?.("(prefers-color-scheme: dark)").matches ? "dark" : "light";
};

export const AppProvider = ({ children }) => {
  const [currentUser, setCurrentUser] = useState(null);
  const [authState, setAuthState] = useState("checking");
  const [theme, setTheme] = useState(getInitialTheme);
  const listenersRef = useRef(new Set());

  const refreshUser = async () => {
    try {
      const { data } = await axios.get(`${API}/auth/me`);
      setCurrentUser(data);
      setAuthState("authed");
      return data;
    } catch {
      setCurrentUser(null);
      setAuthState("anon");
      return null;
    }
  };

  useEffect(() => {
    if (window.location.hash?.includes("session_id=")) {
      setAuthState("anon");
      return;
    }
    refreshUser();
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    const root = document.documentElement;
    root.classList.toggle("dark", theme === "dark");
    localStorage.setItem("sb_theme", theme);
  }, [theme]);

  const logout = async () => {
    try { await axios.post(`${API}/auth/logout`, {}); } catch {}
    setCurrentUser(null);
    setAuthState("anon");
  };

  const toggleTheme = () => setTheme((t) => (t === "dark" ? "light" : "dark"));

  // WS event fanout
  const onEvent = useCallback((ev) => {
    listenersRef.current.forEach((fn) => { try { fn(ev); } catch {} });
  }, []);
  const { connected, send } = useSocket({ enabled: !!currentUser, onEvent });

  const subscribe = useCallback((fn) => {
    listenersRef.current.add(fn);
    return () => listenersRef.current.delete(fn);
  }, []);

  return (
    <AppContext.Provider value={{
      currentUser, setCurrentUser, authState, refreshUser, logout,
      theme, toggleTheme,
      socketConnected: connected, sendSocket: send, subscribe,
    }}>
      {children}
    </AppContext.Provider>
  );
};

export const useApp = () => useContext(AppContext);
